import { authentik } from "@ecca/vue-auth-plugin";
import { defineStore } from "pinia";
import { RouteLocationNormalized } from "vue-router";
import { useTheme } from "vuetify";
import { drawerClientItems } from "@/data/menu/drawerClientItems";
import {
  productItem,
  reportItem,
  contentItem,
  channelItem,
  drawerDefaultItems,
} from "@/data/menu/drawerDefaultItems";
import { initMessaging } from "@/plugins/fcm";
import router from "@/router";
import { cloneDeep } from "@/shared/utils/helpers";

export interface SnackbarProps {
  timeout?: number;
  closeIcon?: boolean;
  icon?: string;
  restrictToRoute?: string;
}

export interface Snackbar extends SnackbarProps {
  type: string;
  text: string;
  visible?: boolean;
}

export interface BreadcrumbsItem {
  text: string;
  link?: { name: string; to?: any; params?: any };
}

export type ErrorLevel = "minor" | "global";
export interface AppError {
  hasError: boolean;
  level?: ErrorLevel;
  message?: string;
}

const errorDefaultState: AppError = {
  hasError: false,
  level: "minor",
  message: undefined,
};

const errorsDefaultState = {
  api: { ...errorDefaultState },
  notFound: { ...errorDefaultState },
  forbidden: { ...errorDefaultState },
};

export const useAppStore = defineStore("ecca-main", {
  state: () => ({
    theme: "greenTheme" as string,
    notifications: [] as Snackbar[],
    drawer: {
      drawerItems: [],
      drawerOffset: 0 as number,
      drawerItemHovered: null as any,
    },
    breadcrumbs: {
      items: [] as BreadcrumbsItem[],
      isVisible: false as boolean,
    },
    errors: cloneDeep(errorsDefaultState),
    editState: {
      isWorkInProgress: false as boolean,
      isWorkInProgressModalOpen: false as boolean,
    },
    cachedTargetRoute: {} as RouteLocationNormalized,
    isLoading: false,
    bottomActionBar: {
      isVisible: false,
      height: 0,
    },
    notificationDialogOpen: false as boolean,
  }),
  getters: {
    userInfo: () => authentik?.userInfo,
    apiError: (state) => state.errors.api.hasError,
    forbidden: (state) => state.errors.forbidden.hasError,
    notFound: (state) => state.errors.notFound.hasError,
    clientID: () => authentik.userInfo?.clientID,
    getDefaultClientMenuItems: () => {
      if (useAppStore().auth("admin")) return drawerClientItems();
      if (useAppStore().auth("client", true)) return drawerClientItems();
    },
    getDefaultMenuItems: (): any[] => {
      if (useAppStore().auth("admin")) return drawerDefaultItems();

      const items = [] as any[];

      if (useAppStore().auth("products")) items.push(productItem);

      if (useAppStore().auth("reporting")) items.push(reportItem);

      if (
        useAppStore().auth("contents:skl") ||
        useAppStore().auth("contents:storyboards") ||
        useAppStore().auth("contents:rich") ||
        useAppStore().auth("contents:written_content")
      )
        items.push(contentItem);

      if (useAppStore().auth("channels")) items.push(channelItem);

      return items;
    },
    getNotificationPermision: () => {
      return Notification.permission;
    },
    hasGlobalError: (state) => {
      return (
        (state.errors.api.hasError && state.errors.api.level === "global") ||
        (state.errors.forbidden.hasError &&
          state.errors.forbidden.level === "global") ||
        (state.errors.notFound.hasError &&
          state.errors.notFound.level === "global")
      );
    },
  },
  actions: {
    async setClientID(
      clientID: string,
      { callBackUrl }: { callBackUrl?: string } = {},
    ) {
      if (
        !this.userInfo?.clients?.some((client) => client.clientID === clientID)
      ) {
        this.setForbiddenError(true);
        this.drawer.drawerItems = [];
        return;
      }

      this.isLoading = true;
      await authentik.authenticateForClient(clientID);
      this.isLoading = false;

      if (callBackUrl) {
        router.push(callBackUrl);
      }
      return;
    },

    setTheme(value: string) {
      const theme = useTheme();

      this.theme = value;
      theme.global.name.value = this.theme;
    },

    setApiError() {
      this.errors.api.hasError = true;
    },

    setForbiddenError(
      value: boolean = true,
      params: {
        level?: ErrorLevel;
        message?: string;
      } = {
        level: "minor",
      },
    ) {
      if (!value) {
        this.errors.forbidden = { ...errorDefaultState };
        return;
      }

      this.errors.forbidden.hasError = value;
      this.errors.forbidden.level = params.level;
      this.errors.forbidden.message = params.message;
    },

    setNotFoundError(
      value: boolean = true,
      params: { level?: ErrorLevel; message?: string } = { level: "minor" },
    ) {
      if (!value) {
        this.errors.notFound = { ...errorDefaultState };
        return;
      }

      this.errors.notFound.hasError = value;
      this.errors.notFound.level = params.level;
      this.errors.notFound.message = params.message;
    },

    resetErrors() {
      this.errors = cloneDeep(errorsDefaultState);
    },

    setDrawerItems(value: any) {
      this.drawer.drawerItems = value;
    },

    setDrawerOffset(value: number) {
      this.drawer.drawerOffset = value;
    },

    setDrawerItemHovered(value: any) {
      this.drawer.drawerItemHovered = value;
    },

    addNotifications(item: Snackbar) {
      const notification = {
        ...item,
        visible: true,
      };

      if (
        this.notifications.some(
          (n: any) =>
            n.icon === notification.icon &&
            n.type === notification.type &&
            n.text === notification.text,
        )
      )
        return;

      this.notifications.push(notification);
    },

    hideNotifications() {
      this.notifications.forEach((s: any) => {
        s.visible = false;
      });
    },

    showNotifications() {
      this.notifications.forEach((s: any) => {
        s.visible = true;
      });
    },

    removeNotifications() {
      this.notifications.forEach((s: any, i) => {
        if (!s.visible) this.notifications.splice(i, 1);
      });
    },

    removeNotificationByText(text: string) {
      this.notifications.forEach((s: any, i) => {
        if (s.text.includes(text)) this.notifications.splice(i, 1);
      });
    },

    removeRestrictedNotifications(routeName: string) {
      let found = false;
      this.notifications.forEach((s: any) => {
        if (!s.restrictToRoute) return;
        if (s.restrictToRoute !== routeName) {
          s.visible = false;
          found = true;
        }
      });
      if (found) this.removeNotifications();
    },

    showNotificationDialog(value: boolean) {
      this.notificationDialogOpen = value;
    },

    logout() {
      authentik.logout();
    },

    setCachedTargetRoute(route: RouteLocationNormalized) {
      this.cachedTargetRoute = route;
    },

    setWorkInProgress(value: boolean) {
      this.editState.isWorkInProgress = value;
    },

    setIsWorkInProgressModalOpen(value: boolean) {
      this.editState.isWorkInProgressModalOpen = value;
    },

    auth: (requestedScope: string, checkWrite?: boolean) => {
      const userScope = authentik.userInfo?.scope ?? "";
      const scopeArr =
        typeof userScope === "string" ? userScope.split(" ") : userScope;

      if (scopeArr.includes("admin")) return true;
      if (requestedScope === "admin") return false;

      if (checkWrite)
        return scopeArr.includes(
          requestedScope.includes(":write")
            ? requestedScope
            : `${requestedScope}:write`,
        );

      return scopeArr.includes(
        /:read|:write/.test(requestedScope)
          ? requestedScope
          : `${requestedScope}:read`,
      );
    },

    validateNotificationPermission: async () => {
      const notifications = await navigator.permissions.query({
        name: "notifications",
      });

      if (notifications.state !== "granted") {
        useAppStore().showNotificationDialog(true);
      } else {
        await initMessaging();
      }
    },
  },
});
